import type { FC } from "react";
import { useState } from "react";

import type { StateOrName } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";

import { UseCaseHomepage, IconButton } from "@similarweb/ui-components";

import { AutocompleteDigitalMarketing } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketing";
import { KeywordGroupCreationDropdown } from "pro-features/components/GroupCreationDropdown/src/KeywordGroupCreationDropdown";
import { FeedbackSideTab } from "app/components/React/sideTabs";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import {
    navigateToKeywordAnalysisOverview,
    navigateToKeywordGenerator,
} from "app/pages/digital-suite/home-page/components/CustomAssets/customAssetsUtils";
import { CustomAssetsWidget } from "app/pages/digital-suite/home-page/components/CustomAssets/CustomAssetsWidget";
import { AssetName } from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { AutocompleteWrapper } from "app/pages/digital-suite/home-page/components/HomePageTrialNTLayout/DigitalSuiteTrialHomePageStyled";
import { KeywordsWebsitesRecentsWidget } from "app/pages/digital-suite/home-page/components/RecentsWidget/KeywordsWebsitesRecentsWidget";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { ScrollContainer } from "app/components/layout/ScrollContainer";
import { BasicDurations } from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SecondaryHomePageHeaderImageUrl } from "app/UtilitiesAndConstants/Constants/Images";
import { RewardsSideBarContainer } from "app/components/React/sideTabs/RewardsSideBarContainer";

const WEBSITE_NAVIGATION_STATE_SINGLE = "organicsearch_seo_overview";
const WEBSITE_NAVIGATION_STATE_COMPARE = "organicsearch_websites_v2_traffic_keywords";

interface ISearchHomePageProps {
    filterRecentsBy?: StateOrName | StateOrName[];
}

export const SearchHomePage: FC<ISearchHomePageProps> = ({ filterRecentsBy }) => {
    const { state } = useCurrentStateAndParams();
    const [, setAutocompleteOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleItemClick = (key: string) => {
        const isCompareMode = key.split(",")?.length > 1;
        const targetState = isCompareMode ? WEBSITE_NAVIGATION_STATE_COMPARE : WEBSITE_NAVIGATION_STATE_SINGLE;

        const defaultParams = getDefaultParamsForState(targetState);

        const navigationParams = {
            ...defaultParams,
            key,
        };

        void proNavigator.go(targetState, navigationParams);
    };

    const shouldNavigate = (key: string) => {
        const isCompareMode = key.split(",")?.length > 1;
        return !isCompareMode;
    };

    return (
        <ScrollContainer>
            <UseCaseHomepage
                title={translate(state.pageTitle as string)}
                titlePosition="left-aligned"
                subtitle={translate("search.websiteanalysis.homepage.subtitle")}
                headerImageUrl={SecondaryHomePageHeaderImageUrl}
                searchComponents={
                    <AutocompleteWrapper>
                        <AutocompleteDigitalMarketing
                            includeKeywordsData
                            maxCompareItemsToDisplay={1}
                            onAutocompleteOpen={() => setAutocompleteOpen(true)}
                            onAutocompleteClose={() => setAutocompleteOpen(false)}
                            websiteState={WEBSITE_NAVIGATION_STATE_SINGLE}
                            onItemClick={handleItemClick}
                            shouldNavigate={shouldNavigate}
                        />
                    </AutocompleteWrapper>
                }
                listItems={[
                    <KeywordsWebsitesRecentsWidget
                        key="recents"
                        filterRecentsBy={filterRecentsBy}
                    />,
                    <CustomAssetsWidget
                        title={translate("search.websiteanalysis.homepage.customassets.title")}
                        key="assets"
                        assetTypes={[AssetName.KeywordLists]}
                        emptyStateTypes={[AssetName.KeywordLists]}
                        ctaButton={
                            <KeywordGroupCreationDropdown
                                hideNotification
                                hasKeywordsGenerator
                                onGeneratorToolClick={() =>
                                    navigateToKeywordGenerator({
                                        country: "999",
                                        duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                                    })
                                }
                                onKeywordsModalClick={() => setIsModalOpen(true)}
                                dropdownPopupPlacement={"ontop-right"}
                                bubbleDirection={"right"}
                            >
                                <IconButton
                                    type="outlined"
                                    iconName="plus"
                                    placement="left"
                                    iconSize="9px"
                                >
                                    <I18n>keywordresearch.monitorkeywords.button.create</I18n>
                                </IconButton>
                            </KeywordGroupCreationDropdown>
                        }
                    />,
                    <KeywordsGroupEditorModal
                        key="keyword-list-editor"
                        onClose={() => setIsModalOpen(false)}
                        open={isModalOpen}
                        onSave={({ Id }) => navigateToKeywordAnalysisOverview(Id)}
                        showGoToMonitorKeywordListButton
                    />,
                ]}
            />
            <FeedbackSideTab />
            <RewardsSideBarContainer />
        </ScrollContainer>
    );
};
